// * React Hooks
import React, { useEffect, useContext, useRef } from 'react'
// * Style
import './scss/style.scss'

import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom'

// * Application Router Component
import Router from './Router/Router'

import { getCSRFToken, setCSRFToken } from './helpers'
import { fetchCSRFToken } from './api/csrf'

// * React tostify for notifications.
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// * API Handler Initialization
import { QueryClient, QueryClientProvider } from 'react-query'
import { AuthContextProvider } from './helpers/AuthContext'
import AuthContext from './helpers/AuthContext'
import jwt_decode from 'jwt-decode'

const queryClint = new QueryClient()

// Component to handle URL token extraction
function AppContent() {
  const { login } = useContext(AuthContext)
  const location = useLocation()
  const navigate = useNavigate()
  const processedSearchRef = useRef('')

  useEffect(() => {
    const searchParams = location.search
    if (searchParams && searchParams.length > 1 && processedSearchRef.current !== searchParams) {
      processedSearchRef.current = searchParams
      
      const currentPathname = location.pathname
      const urlParams = new URLSearchParams(searchParams)
      let token = urlParams.get('access_token')

      // Fallback: check if the entire search string is the token (original behavior)
      if (!token && searchParams.length > 1) {
        const potentialToken = searchParams.substring(1)
        if (potentialToken.includes('.') && !potentialToken.includes('=')) {
          token = potentialToken
        }
      }

      // Check if it looks like a JWT token (contains dots)
      if (token && token.includes('.')) {
        try {
          // Validate token by decoding it
          const decodedToken = jwt_decode(token)
          const currentDate = new Date()
          
          // Check if token is expired
          if (decodedToken.exp * 1000 < currentDate.getTime()) {
            console.log('Token from URL is expired.')
            // Clean URL without token
            window.history.replaceState({}, '', currentPathname)
            return
          }
          
          // Token is valid, store it in localStorage
          // The decoded token contains: role, user_level, and id
          const tokenData = JSON.stringify({ token: token })
          localStorage.setItem('authToken', tokenData)
          const verifyStorage = localStorage.getItem('authToken')
          if (!verifyStorage) {
            console.error('Failed to store token in localStorage!')
            return
          }
          
          console.log('Token stored successfully in localStorage')
          
          // Use login function to set user state
          login({ token: token })
          
          console.log('User info from token:', {
            role: decodedToken.role,
            user_level: decodedToken.user_level,
            id: decodedToken.id
          })
          
          // Wait a bit to ensure state is set before navigation
          setTimeout(() => {
            // Verify token is still there after login
            const tokenStillThere = localStorage.getItem('authToken')
            if (!tokenStillThere) {
              console.error('Token was removed after login!')
              return
            }
            
            // Clean URL by removing the token
            window.history.replaceState({}, '', currentPathname)
            
            // Redirect to dashboard if not already there
            if (currentPathname === '/' || currentPathname === '/login') {
              navigate('/dashboard', { replace: true })
            }
          }, 100)
        } catch (error) {
          console.error('Invalid token in URL:', error)
          // Clean URL without token
          window.history.replaceState({}, '', currentPathname)
        }
      } else {
        // If search params exist but don't look like a token, clean the URL
        window.history.replaceState({}, '', currentPathname)
      }
    }
  }, [location.search, location.pathname, login, navigate])

  return <Router />
}

function App() {
  useEffect(() => {
    async function checkCSRFToken() {
      console.log('calling csrf')
      const CSRF_TOKEN = await getCSRFToken()
      if (!CSRF_TOKEN) {
        const csrf_token = await fetchCSRFToken()
        setCSRFToken(csrf_token)
      }
    }
    checkCSRFToken()
  }, [])

  return (
    <BrowserRouter>
      <div className="App">
        <QueryClientProvider client={queryClint}>
          <AuthContextProvider>
            <AppContent />
          </AuthContextProvider>
          <ToastContainer />
        </QueryClientProvider>
      </div>
    </BrowserRouter>
  )
}

export default App
